import type { AnyBridgeEvent, BridgeEvent } from './bridge-events-types';

/**
 * Event handler function type.
 */
export type EventHandler<T extends BridgeEvent = AnyBridgeEvent> = (event: T) => void;

/**
 * Event filter predicate function type.
 */
export type EventFilter<T extends BridgeEvent = AnyBridgeEvent> = (event: T) => boolean;

/**
 * Configuration options for the event emitter.
 */
export interface EventEmitterConfig {
	/**
	 * Whether to enable event emission.
	 * @default true
	 */
	enabled?: boolean;
}

/**
 * Event subscription handle for managing listeners.
 */
export interface EventSubscription {
	/**
	 * Unsubscribe from the event.
	 */
	unsubscribe(): void;

	/**
	 * Whether this subscription is still active.
	 */
	isActive(): boolean;
}

/**
 * Core event emitter for bridge API events.
 *
 * This emitter is designed to be lightweight and performant, with minimal
 * overhead when events are not being consumed. All events are domain events,
 * with the intent of enabling loosely coupled internal systems which observe
 * the entire bridge API. E.g., our debugging sub-system.
 */
export class BridgeEventEmitter {
	private handlers = new Map<string, Set<EventHandler>>();
	private globalHandlers = new Set<EventHandler>();
	private config: Required<EventEmitterConfig>;
	private bridgeId: string | null = null;

	constructor(config: EventEmitterConfig = {}) {
		this.config = {
			enabled: true,
			...config,
		};
	}

	/**
	 * Set the bridge ID for this emitter instance.
	 * Should be called once when the bridge API is initialized.
	 *
	 * @param bridgeId The unique identifier for the bridge API instance
	 */
	setBridgeId(bridgeId: string): void {
		this.bridgeId = bridgeId;
	}

	/**
	 * Emit an event to all registered handlers.
	 * Automatically adds timestamp and bridge ID to the event.
	 *
	 * @param event The event to emit (without timestamp and bridgeId)
	 */
	emit(event: AnyBridgeEvent): void {
		// Early return if emitter is disabled
		if (!this.config.enabled) {
			return;
		}

		// Automatically add timestamp and bridge ID
		const fullEvent = {
			...event,
			timestamp: Date.now(),
			bridgeId: this.bridgeId ?? 'unknown-bridge',
		};

		// Emit to type-specific handlers
		const typeHandlers = this.handlers.get(fullEvent.type);
		if (typeHandlers) {
			for (const handler of typeHandlers) {
				this.safeInvoke(handler, fullEvent);
			}
		}

		// Emit to global handlers
		for (const handler of this.globalHandlers) {
			this.safeInvoke(handler, fullEvent);
		}
	}

	/**
	 * Listen for events of a specific type.
	 *
	 * @param eventType The type of event to listen for
	 * @param handler The handler function to call when the event occurs
	 * @returns A subscription handle
	 */
	on<T extends AnyBridgeEvent>(eventType: T['type'], handler: EventHandler<T>): EventSubscription {
		if (!this.handlers.has(eventType)) {
			this.handlers.set(eventType, new Set());
		}

		const handlers = this.handlers.get(eventType)!;
		handlers.add(handler as EventHandler);

		return this.createSubscription(() => {
			handlers.delete(handler as EventHandler);
			if (handlers.size === 0) {
				this.handlers.delete(eventType);
			}
		});
	}

	/**
	 * Listen for all events regardless of type.
	 *
	 * @param handler The handler function to call for any event
	 * @returns A subscription handle
	 */
	onAny(handler: EventHandler<AnyBridgeEvent>): EventSubscription {
		this.globalHandlers.add(handler);

		return this.createSubscription(() => {
			this.globalHandlers.delete(handler);
		});
	}

	/**
	 * Remove a specific handler for an event type.
	 *
	 * @param eventType The event type to remove the handler from
	 * @param handler The handler to remove
	 */
	off<T extends AnyBridgeEvent>(eventType: T['type'], handler: EventHandler<T>): void {
		const handlers = this.handlers.get(eventType);
		if (handlers) {
			handlers.delete(handler as EventHandler);
			if (handlers.size === 0) {
				this.handlers.delete(eventType);
			}
		}
	}

	/**
	 * Remove all handlers for a specific event type.
	 *
	 * @param eventType The event type to clear handlers for
	 */
	removeAllListeners(eventType?: string): void {
		if (eventType) {
			this.handlers.delete(eventType);
		} else {
			this.handlers.clear();
			this.globalHandlers.clear();
		}
	}

	/**
	 * Update emitter configuration.
	 *
	 * @param config New configuration options
	 */
	configure(config: Partial<EventEmitterConfig>): void {
		this.config = { ...this.config, ...config };
	}

	/**
	 * Enable or disable the emitter.
	 *
	 * @param enabled Whether to enable event emission
	 */
	setEnabled(enabled: boolean): void {
		this.config.enabled = enabled;
	}

	/**
	 * Check if the emitter is currently enabled.
	 */
	isEnabled(): boolean {
		return this.config.enabled;
	}

	/**
	 * Destroy the emitter and clean up all resources.
	 */
	destroy(): void {
		this.handlers.clear();
		this.globalHandlers.clear();
		this.config.enabled = false;
	}

	/**
	 * Safely invoke a handler, catching any errors.
	 */
	private safeInvoke(handler: EventHandler, event: AnyBridgeEvent): void {
		try {
			handler(event);
		} catch {
			// we do not want to produce error logs for the observer
			// if you need to add logs, go for it - just remove them afterwards!
		}
	}

	/**
	 * Create a subscription handle.
	 */
	private createSubscription(unsubscribeFn: () => void): EventSubscription {
		let active = true;

		return {
			unsubscribe: () => {
				if (active) {
					unsubscribeFn();
					active = false;
				}
			},
			isActive: () => active,
		};
	}
}

/**
 * Default global event emitter instance.
 *
 * This can be used as a singleton across the bridge API, or individual
 * bridge instances can create their own emitters.
 */
let globalEmitter: BridgeEventEmitter | null = null;

/**
 * Get or create the global event emitter instance.
 */
export function emitter(): BridgeEventEmitter {
	if (!globalEmitter) {
		globalEmitter = new BridgeEventEmitter();
	}
	return globalEmitter;
}

/**
 * Destroy the global event emitter instance.
 */
export function destroyGlobalEventEmitter(): void {
	if (globalEmitter) {
		globalEmitter.destroy();
		globalEmitter = null;
	}
}
